/**
 * Typed media client for `@proof/client-core` — A-WP1 (Photos v1).
 *
 * Wraps the oRPC `media.*` namespace with a narrow, hand-written surface so the
 * web tree compiles and unit-tests against a mock without depending on the full
 * generated `AppRouter` type (the same structural pattern `collections.ts` uses
 * to sidestep the pre-existing router-context type mismatch — any real
 * `ApiClient` satisfies this interface at runtime because the oRPC proxy
 * forwards calls regardless of TS router-shape inference).
 *
 * The transport is the shared `createApiClient` (credentials: include + CSRF),
 * so every mutation rides the session cookie. No new transport.
 *
 * Serving note: in-app display uses the session-minted `mintGetUrl` token path
 * (`GET /media/:uuid/:variant?token=`). Variant SIZING is applied by the single
 * `withImageTransform` helper in the web tree (Cloudflare Images transformations,
 * amendment 2026-07-10) — kept out of this transport client so the zone config
 * can land independently.
 *
 * Refs:
 *   .plan/implementation/2026-07-10-track-a-record-build-plan.md A-WP1
 *   architecture.md §3 — apps/api owns the oRPC router; clients consume it.
 */

// ── Wire types ─────────────────────────────────────────────────────────────────

export type MediaParentKind = "attempt" | "artefact" | "batch";

/** The three permanently-stored derivative variants (no `original` is served). */
export type MediaVariant = "thumb-256" | "card-768" | "full-1600";

/**
 * One media row as the client consumes it. Mirrors `MediaItem` in
 * `apps/api/src/routes/media/list.ts` — booleans, not the DO's 0/1 ints.
 */
export interface MediaItem {
  uuid: string;
  parentKind: string;
  parentUuid: string;
  role: string | null;
  width: number | null;
  height: number | null;
  createdAt: number;
  /** Derivatives are ready to serve (EXIF strip + generation complete). */
  exifStripped: boolean;
  /** Processing failed permanently — offer "remove + re-upload". */
  processingFailed: boolean;
}

/** Structural interface covering the `media.*` oRPC procedures the UI needs. */
export interface MediaApiSurface {
  media: {
    mintPutUrl(input: {
      parentKind: MediaParentKind;
      parentUuid: string;
      mediaUuid: string;
      fileExt: string;
      sha256: string;
      width?: number | null;
      height?: number | null;
      captureTs?: number | null;
      role?: string | null;
    }): Promise<{ putUrl: string; stagingKey: string; mediaUuid: string }>;
    finalize(input: {
      parentKind: MediaParentKind;
      parentUuid: string;
      mediaUuid: string;
      fileExt: string;
      sha256: string;
      width?: number | null;
      height?: number | null;
      captureTs?: number | null;
      role?: string | null;
    }): Promise<{ ok: boolean; media: unknown }>;
    mintGetUrl(input: {
      mediaUuid: string;
      variant: MediaVariant;
    }): Promise<{ token: string; expiresAt: number; mediaUuid: string; variant: MediaVariant }>;
    list(input: {
      parentKind: MediaParentKind;
      parentUuids: string[];
    }): Promise<{ media: Record<string, MediaItem[]> }>;
    delete(input: { uuid: string }): Promise<{ ok: true }>;
  };
}

// ── Factory ─────────────────────────────────────────────────────────────────────

/**
 * Create a typed media client bound to an existing oRPC `ApiClient`.
 *
 * Usage:
 *   const api = createApiClient({ baseUrl: "" });
 *   const media = createMediaClient(api);
 *   const { media: byParent } = await media.listForParents("attempt", [uuidA, uuidB]);
 */
export function createMediaClient(api: MediaApiSurface) {
  return {
    /**
     * List media grouped by parent for one or many parents in ONE round-trip.
     * Every requested uuid is a key in the result (empty array when it has none).
     */
    async listForParents(
      parentKind: MediaParentKind,
      parentUuids: string[],
    ): Promise<Record<string, MediaItem[]>> {
      if (parentUuids.length === 0) return {};
      const { media } = await api.media.list({ parentKind, parentUuids });
      return media;
    },

    /** List media for a single parent (thin convenience over the batched call). */
    async listForParent(parentKind: MediaParentKind, parentUuid: string): Promise<MediaItem[]> {
      const grouped = await this.listForParents(parentKind, [parentUuid]);
      return grouped[parentUuid] ?? [];
    },

    /** Mint a short-lived HMAC download token for a derivative variant. */
    async mintGetUrl(
      mediaUuid: string,
      variant: MediaVariant,
    ): Promise<{ token: string; expiresAt: number }> {
      const res = await api.media.mintGetUrl({ mediaUuid, variant });
      return { token: res.token, expiresAt: res.expiresAt };
    },

    /** Delete a media row and purge its R2 derivatives. */
    async deleteMedia(uuid: string): Promise<{ ok: true }> {
      return api.media.delete({ uuid });
    },
  };
}

export type MediaClient = ReturnType<typeof createMediaClient>;

// ── Quota-error detection (Rule 8) ───────────────────────────────────────────────

/**
 * The axis + cap detail a `QUOTA_EXCEEDED` error carries, when present.
 */
export interface MediaQuotaDetail {
  axis?: string;
  current?: number;
  cap?: number;
}

/**
 * True when a thrown error is the media quota breach (429 / `QUOTA_EXCEEDED`).
 *
 * The finalize route throws `ORPCError("TOO_MANY_REQUESTS", { message:
 * "QUOTA_EXCEEDED", data: { axis, current, cap } })`. oRPC surfaces the code and
 * message on the client error; some transports also expose a numeric status.
 * We match defensively on all three so a route-intercepted 429 (Playwright
 * acceptance #6) is detected identically to a real cap breach.
 */
export function isQuotaError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: unknown; status?: unknown; message?: unknown };
  if (e.code === "TOO_MANY_REQUESTS") return true;
  if (e.status === 429) return true;
  if (typeof e.message === "string" && e.message.includes("QUOTA_EXCEEDED")) return true;
  return false;
}

/**
 * True when a thrown error is the server-side upload-unavailability signal
 * (503 / `uploads_unavailable`).
 *
 * The mintPutUrl route throws `ORPCError("SERVICE_UNAVAILABLE", { message:
 * "uploads_unavailable" })` when the R2 presigning secrets are absent from the
 * Worker — a provisioning gap, NOT a bad photo. We match defensively on the
 * oRPC code, the numeric 503 status some transports expose, and the stable
 * message, mirroring `isQuotaError` so the two machine-readable server signals
 * are discriminated identically. This lets the UI say "uploads are unavailable"
 * instead of blaming the user's file (root cause of the 2026-07 dev outage,
 * where this surfaced as a generic per-photo "failed" toast).
 */
export function isUploadsUnavailableError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: unknown; status?: unknown; message?: unknown };
  if (e.code === "SERVICE_UNAVAILABLE") return true;
  if (e.status === 503) return true;
  if (typeof e.message === "string" && e.message.includes("uploads_unavailable")) return true;
  return false;
}

/** Extract the axis/cap detail from a quota error, if the route attached it. */
export function quotaDetail(err: unknown): MediaQuotaDetail {
  if (!err || typeof err !== "object") return {};
  const data = (err as { data?: unknown }).data;
  if (!data || typeof data !== "object") return {};
  const d = data as MediaQuotaDetail;
  const out: MediaQuotaDetail = {};
  if (typeof d.axis === "string") out.axis = d.axis;
  if (typeof d.current === "number") out.current = d.current;
  if (typeof d.cap === "number") out.cap = d.cap;
  return out;
}
